import * as React from 'react';
import { History } from 'history';
import { Link } from 'react-router-dom';
import * as IDatabase from '../../../types/database';

import { IDatabaseSetupSession } from './index';
import './styles.css';
import sectionImage from '../../../images/section.png'
import tabsImage from '../../../images/layout-tab.png'
import columnLayoutImage from '../../../images/layout-column.png'

import { InjectedIntlProps, injectIntl, defineMessages, FormattedMessage } from 'react-intl';
import Form from 'src/client/pages/Form';

interface IProps {
    getSavedSession: () => Promise<IDatabaseSetupSession | null>;
    history: History;
    handleCreate: () => void;
    handleCancel: () => void;
    routes: Array<{ name: string, id: string, path: string, component: ({ history }: { history: History }) => any }>
}

class Review extends React.Component<IProps & InjectedIntlProps, IDatabaseSetupSession> {
    messages = defineMessages({
        yes: {
            id: 'db.review.yes',
            defaultMessage: 'Yes'
        },
        no: {
            id: 'db.review.no',
            defaultMessage: 'No'
        },
        public: {
            id: 'db.review.access.public',
            defaultMessage: 'Public Access'
        },
        private: {
            id: 'db.review.access.private',
            defaultMessage: 'Private Access'
        },
        fieldName: {
            id: 'db.review.field.name',
            defaultMessage: 'Field Name'
        },
        groupName: {
            id: 'db.review.group.name',
            defaultMessage: 'Section Name'
        },
        fieldReq: {
            id: 'db.review.field.req',
            defaultMessage: 'Required'
        },
        fieldValues: {
            id: 'db.review.field.values',
            defaultMessage: 'Pre-defined Values'
        },
        fieldType: {
            id: 'db.review.field.type',
            defaultMessage: 'Selection Type'
        },
        edit: {
            id: 'db.review.section.edit',
            defaultMessage: 'Edit {section}'
        },
        general: {
            id: 'db.review.section.general',
            defaultMessage: 'General Info'
        },
        fields: {
            id: 'db.review.section.fields',
            defaultMessage: 'Database Fields'
        },
        search: {
            id: 'db.review.section.search',
            defaultMessage: 'Search Filters'
        },
        layout: {
            id: 'db.review.section.layout',
            defaultMessage: 'Information Page Settings'
        },
        sectionsLayout: {
            id: 'db.review.section.layout.sections',
            defaultMessage: 'Layout sections vertically'
        },
        tabsLayout: {
            id: 'db.review.section.layout.tabs',
            defaultMessage: 'Layout sections in horizontal tabs'
        }
    });

    translate = (msg: FormattedMessage.MessageDescriptor) => {
        return this.props.intl.formatMessage(msg);
    };

    async componentDidMount() {
        const session = await this.props.getSavedSession();
        if (session) {
            this.setState({ ...session });
        }
    }

    handleGoBack = () => {
        this.props.history.goBack();
    };

    renderFieldRow = (field: IDatabase.IField) => {
        return (
            <tr key={`${field.label}`} className="trcontent">
                <td><b>{field.group}</b></td>
                <td><b>{field.label}</b></td>
                <td>{field.isRequired ? this.translate(this.messages.yes) : this.translate(this.messages.no)}</td>
                <td>{field.type}</td>
                <td>{field.predefined ? this.translate(this.messages.yes) : this.translate(this.messages.no)}</td>
            </tr>
        );
    };

    renderDatabaseFields = () => {
        if (
            this.state &&
            this.state.fields &&
            this.state.fields &&
            this.state.fields.selected &&
            this.state.fields.selected &&
            Array.isArray(this.state.fields.selected)
        ) {
            const result: any = [];
            this.state.fields.selected.forEach(section => {
                result.push(section.fields.map(this.renderFieldRow));
            });
            return result;
        }
        return null;
    };

    renderSelectedSearchFilters = () => {
        if (this.state && this.state.search) {
            return this.state.search.selected.map(field => {
                return <span key={`search-field-${field.group || ''}-${field.name}`}>{field.group} - {field.label}</span>;
            });
        }
        return null;
    };

    renderSelectedLayout = () => {
        if (this.state && this.state.layout) {
            switch (this.state.layout.layout) {
                case 'two':
                    return (
                        <tr>
                            <th><img src={tabsImage} /></th>
                            <td>{this.props.intl.formatMessage(this.messages.tabsLayout)}</td>
                        </tr>
                    );
                case 'columns':
                    return (
                        <tr>
                            <th><img height={150} src={columnLayoutImage} /></th>
                            <td>{'Two Column Layout'}</td>
                        </tr>
                    );
                default:
                    return (
                        <tr>
                            <th><img src={tabsImage} /></th>
                            <td>{this.props.intl.formatMessage(this.messages.sectionsLayout)}</td>
                        </tr>
                    );
            }
        }
        return null;
    };

    renderAllowedAttachments = () => {
        if (this.state && this.state.layout) {
            return (
                <tr>
                    <FormattedMessage tagName="th" id="db.review.layout.attach" defaultMessage="Allow attachments:" />
                    <td>{this.props.intl.formatMessage(this.state.layout.allowAttachments ? this.messages.yes : this.messages.no)}</td>
                </tr>
            );
        }
        return null;
    };

    renderGeneralInfo = () => {
        if (this.state && this.state.general) {
            return (
                <div>
                    <div className="row">
                        <div className="col-lg-3 col-md-6 col-sm-12">
                            <div className="infosec">
                                <p>
                                    <span className="text-muted"><FormattedMessage id="db.review.category" defaultMessage="Database Category" /></span>{' '}
                                    <span>{this.state.general.category}</span>
                                </p>
                            </div>
                        </div>
                    </div>

                    <div className="row">
                        <div className="col-lg-3 col-md-6 col-sm-12">
                            <div className="infosec">
                                <p>
                                    <span className="text-muted"><FormattedMessage id="db.review.title" defaultMessage="Database Title" /></span>{' '}
                                    <span>{this.state.general.name}</span>
                                </p>
                            </div>
                        </div>
                        <div className="col-lg-3 col-md-6 col-sm-12">
                            <div className="infosec">
                                <p>
                                    <span className="text-muted"><FormattedMessage id="db.review.link" defaultMessage="Database Link" /></span>{' '}
                                    <span>{this.state.general.link}</span>
                                </p>
                            </div>
                        </div>
                    </div>

                    <div className="row">
                        <div className="col-lg-3 col-md-6 col-sm-12">
                            <div className="infosec">
                                <p>
                                    <span className="text-muted"><FormattedMessage id="db.review.access" defaultMessage="End User Access" /></span>{' '}
                                    <span>
                                        {this.state.general.isPublic
                                            ? this.translate(this.messages.public)
                                            : this.translate(this.messages.private)}
                                    </span>
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            );
        }
        return null;
    };

    render() {
        return (
            <div>
                <div className="row px-2">
                    <h5><FormattedMessage id="db.review.hdr" defaultMessage="5. Review" /></h5>
                </div>
                <div className="row text-muted px-2 py-3">
                    <p className="text-muted">
                        <FormattedMessage id="db.review.txt" defaultMessage={`
                        Please review the database. Click on the edit icon to
                        change information in a particular section.
                        `} />
                    </p>
                </div>

                <div className="container-fluid">
                    <div className="row">
                        <div className="col-lg-12">
                            <div className="sigupInner">
                                <div className="formContainer">
                                    <div className="general-info-area">
                                        <div className="review-title">
                                            <h3>{this.props.intl.formatMessage(this.messages.general)}</h3>
                                            <Link to={(this.props.routes.find(route => route.id === 'general') || { path: '/client/database/setup/general' }).path}
                                                title={this.props.intl.formatMessage(this.messages.edit, { 'section': this.props.intl.formatMessage(this.messages.general) })}>
                                                <i className="fas fa-edit" />
                                            </Link>
                                        </div>
                                        {this.renderGeneralInfo()}
                                    </div>
                                    <div className="general-info-area">
                                        <div className="review-title">
                                            <h3>{this.props.intl.formatMessage(this.messages.fields)}</h3>
                                            <Link to={(this.props.routes.find(route => route.id === 'fields') || { path: '/client/database/setup/fields' }).path}
                                                title={this.props.intl.formatMessage(this.messages.edit, { 'section': this.props.intl.formatMessage(this.messages.fields) })}>
                                                <i className="fas fa-edit" />
                                            </Link>
                                        </div>

                                        <div className="admintable">
                                            <table>
                                                <tbody>
                                                    <tr className="trhead">
                                                        <td>{this.translate(this.messages.groupName)}</td>
                                                        <td>{this.translate(this.messages.fieldName)}</td>
                                                        <td>{this.translate(this.messages.fieldReq)}</td>
                                                        <td>{this.translate(this.messages.fieldType)}</td>
                                                        <td>{this.translate(this.messages.fieldValues)}</td>
                                                    </tr>
                                                    {this.renderDatabaseFields()}
                                                </tbody>
                                            </table>
                                        </div>
                                        <div className="mobileadmintable">
                                            <table>
                                                <tbody>
                                                    <tr>
                                                        <td className="trhead">{this.translate(this.messages.fieldName)}</td>
                                                        <td>
                                                            <a href="#">State</a>
                                                        </td>
                                                    </tr>
                                                    <tr>
                                                        <td className="trhead">{this.translate(this.messages.fieldReq)}</td>
                                                        <td>Yes</td>
                                                    </tr>
                                                    <tr>
                                                        <td className="trhead">{this.translate(this.messages.fieldType)}</td>
                                                        <td>Single</td>
                                                    </tr>
                                                    <tr>
                                                        <td className="trhead">{this.translate(this.messages.fieldValues)}</td>
                                                        <td>Yes</td>
                                                    </tr>
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                    <div className="general-info-area">
                                        <div className="review-title">
                                            <h3>{this.props.intl.formatMessage(this.messages.search)}</h3>
                                            <Link to={(this.props.routes.find(route => route.id === 'search') || { path: '/client/database/setup/search' }).path}
                                                title={this.props.intl.formatMessage(this.messages.edit, { 'section': this.props.intl.formatMessage(this.messages.search) })}>
                                                <i className="fas fa-edit" />
                                            </Link>
                                        </div>

                                        <div className="formrow">
                                            <div className="formcol">
                                                <label>
                                                    {this.state && this.state.search && this.state.search.selected.length ?
                                                        <FormattedMessage id="db.review.search.filters" defaultMessage="Selected Search Filters" /> :
                                                        <FormattedMessage id="db.review.search.nofilters" defaultMessage="No Selected Search Filters" />
                                                    }
                                                </label>
                                                <div className="review-filter-values">
                                                    {this.renderSelectedSearchFilters()}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="general-info-area">
                                        <div className="review-title">
                                            <h3>{this.props.intl.formatMessage(this.messages.layout)}</h3>
                                            <Link to={(this.props.routes.find(route => route.id === 'layout') || { path: '/client/database/setup/layout' }).path}
                                                title={this.props.intl.formatMessage(this.messages.edit, { 'section': this.props.intl.formatMessage(this.messages.layout) })}>
                                                <i className="fas fa-edit" />
                                            </Link>
                                        </div>
                                        <div className="formrow">
                                            <div className="formcol">
                                                <table className="db-review-layout">
                                                    <tbody>
                                                        {this.renderSelectedLayout()}
                                                        {this.renderAllowedAttachments()}
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="row border-top">
                    <button onClick={this.props.handleCreate} className="primary">
                        <FormattedMessage id="db.review.save" defaultMessage="Save" />
                    </button>
                    <button className="primary-inv" onClick={this.handleGoBack}>
                        <FormattedMessage id="db.review.back" defaultMessage="Back" />
                    </button>
                    <button className="btn btn-link" onClick={this.props.handleCancel}><FormattedMessage id="db.review.cancel" defaultMessage="Cancel" /></button>
                </div>
            </div>
        );
    }
}
export default injectIntl(Review);


// WEBPACK FOOTER //
// ./src/client/database/setup/Review.tsx